import { createTheme } from '@mui/material/styles';

// Colores corporativos de WolvesLabs
const wolvesLabsColors = {
  primary: '#1a365d', // Azul corporativo profundo
  secondary: '#2c5282', // Azul medio corporativo
  accent: '#3182ce', // Azul corporativo brillante
  highlight: '#63b3ed', // Azul corporativo claro
  tech: '#2b6cb0', // Azul tecnológico corporativo
  light: '#f7fafc', // Gris muy claro
  white: '#ffffff',
  dark: '#1a202c', // Azul muy oscuro corporativo
  gray: {
    50: '#f7fafc',
    100: '#edf2f7',
    200: '#e2e8f0',
    300: '#cbd5e0',
    400: '#a0aec0',
    500: '#718096',
    600: '#4a5568',
    700: '#2d3748',
    800: '#1a202c',
    900: '#171923',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
    secondary: 'linear-gradient(135deg, #3182ce 0%, #2b6cb0 100%)',
    tech: 'linear-gradient(135deg, #2b6cb0 0%, #1a365d 100%)',
    hero: 'linear-gradient(135deg, #1a202c 0%, #1a365d 50%, #3182ce 100%)',
    blue: 'linear-gradient(135deg, #1a365d 0%, #3182ce 100%)',
  }
};

// Tema personalizado de WolvesLabs con colores corporativos azules
export const wolvesLabsTheme = createTheme({
  palette: {
    primary: {
      main: wolvesLabsColors.primary,
      light: wolvesLabsColors.highlight,
      dark: wolvesLabsColors.dark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: wolvesLabsColors.secondary,
      light: wolvesLabsColors.accent,
      dark: wolvesLabsColors.primary,
      contrastText: '#FFFFFF',
    },
    success: {
      main: wolvesLabsColors.tech,
      light: wolvesLabsColors.highlight,
      dark: wolvesLabsColors.primary,
    },
    warning: {
      main: wolvesLabsColors.accent,
      light: wolvesLabsColors.highlight,
      dark: wolvesLabsColors.primary,
    },
    error: {
      main: '#e53e3e',
      light: '#fc8181',
      dark: '#c53030',
    },
    info: {
      main: wolvesLabsColors.accent,
      light: wolvesLabsColors.highlight,
      dark: wolvesLabsColors.primary,
    },
    background: {
      default: wolvesLabsColors.light,
      paper: '#FFFFFF',
    },
    text: {
      primary: wolvesLabsColors.dark,
      secondary: wolvesLabsColors.gray[600],
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      color: wolvesLabsColors.primary,
    },
    h2: {
      fontWeight: 700,
      color: wolvesLabsColors.primary,
    },
    h3: {
      fontWeight: 600,
      color: wolvesLabsColors.secondary,
    },
    h4: {
      fontWeight: 600,
      color: wolvesLabsColors.primary,
    },
    h5: {
      fontWeight: 500,
      color: wolvesLabsColors.secondary,
    },
    h6: {
      fontWeight: 500,
      color: wolvesLabsColors.accent,
    },
  },
  components: {
    // Personalización de AppBar para WolvesLabs
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: wolvesLabsColors.primary,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderBottom: `2px solid ${wolvesLabsColors.accent}`,
          backdropFilter: 'blur(10px)',
          '&.MuiAppBar-colorPrimary': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            color: wolvesLabsColors.primary,
          },
          '&.MuiAppBar-positionFixed': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            color: wolvesLabsColors.primary,
          },
        },
      },
    },
    // Personalización de botones para WolvesLabs
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          border: '2px solid transparent',
          transition: 'all 0.3s ease',
          '&.MuiButton-text': {
            color: wolvesLabsColors.primary,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: wolvesLabsColors.accent,
              color: 'white',
              border: `2px solid ${wolvesLabsColors.highlight}`,
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${wolvesLabsColors.accent}40`,
            },
          },
          '&.MuiButton-textPrimary': {
            color: wolvesLabsColors.primary,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: wolvesLabsColors.accent,
              color: 'white',
            },
          },
        },
      },
    },
    // Personalización de ListItemButton para WolvesLabs
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: 'transparent',
          border: '2px solid transparent',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: wolvesLabsColors.accent,
            border: `2px solid ${wolvesLabsColors.highlight}`,
            transform: 'translateX(8px)',
          },
        },
      },
    },
    // Personalización de Paper para WolvesLabs
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    // Personalización de Drawer para WolvesLabs
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          color: wolvesLabsColors.primary,
          borderRight: `3px solid ${wolvesLabsColors.accent}`,
        },
      },
    },
    // Personalización de Toolbar para WolvesLabs
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          color: wolvesLabsColors.primary,
        },
      },
    },
  },
});

export { wolvesLabsColors };
export default wolvesLabsTheme;